import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import style from '../../styles/auth/RegisterPage.module.css'
import { useDispatch } from 'react-redux';
import { getLogos } from 'src/redux/reducers/imagesReduser';
import { useSelector } from 'react-redux';
import { RootState } from 'src/interface/IRootState';
import { Card, CardContent, TextField, Button, Typography, InputAdornment, IconButton } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { IRegisterData } from 'src/interface/IRegisterData';
import doneNotify from 'src/notifications/doneNotify';
import errorNotify from 'src/notifications/errorNotify';
import { Bounce, ToastContainer } from 'react-toastify';
import verifyService from 'src/function/auth/verifyService';
import useRegister from 'src/hooks/auth/useRegister';
import AuthService from 'src/function/auth/AuthService';


const validationUserDataSchema = Yup.object({
  email: Yup.string().email('Некоректный email').required("Обязательное поле"),
  password: Yup.string().min(6, 'Минимум 6 символов').required("Обязательное поле"),
});

const validationCodeSchema = Yup.object({
  code: Yup.string().required("Обязательное поле"),
});

const RegisterPage: React.FC = () => {
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [onSendCode, setOnSendCode] = useState<boolean>(false);
  const [userEmail, setUserEmail] = useState('');
  const [userPassword, setUserPassword] = useState('');
  const [userTelegramId, setUserTelegramId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [registerUser] = useRegister();

  const togglePasswordVisibility = () => setShowPassword((prev) => !prev);

  const handlerCheckEmail = async (values: IRegisterData) => {
    const { email, password, telegramId } = values;
    setLoading(true);
    try {
      const checkEmailAndTelegramId = await AuthService.isUserExists(email, telegramId);

      if (checkEmailAndTelegramId.error === null) {
        if (!checkEmailAndTelegramId.data.userExistsByEmail && !checkEmailAndTelegramId.data.userExistsByTelegramId) {
          const sendCode = await verifyService.sendEmailVerifyCode(email);

          if (!sendCode.status) {
            setLoading(false);
            throw new Error('send email error');
          }

          doneNotify('Код подтверждения отправлен вам на почту');
          setUserEmail(email);
          setUserPassword(password);
          setUserTelegramId(telegramId);
          setOnSendCode(true);
          setLoading(false);
        } else if (checkEmailAndTelegramId.data.userExistsByEmail) {
          setLoading(false);
          errorNotify('Данная электронная почта занята. Измените почту и попробуйте еще раз');
        } else if (checkEmailAndTelegramId.data.userExistsByTelegramId) {
          setLoading(false);
          errorNotify('Данный ID занят. Измените Telegram ID и попробуйте еще раз');
        }
      }
    } catch (error) {
      errorNotify('Возникла непредвиденная ошибка.');
    }
  }

  const handleSubmitCode = async (values: { code: number }) => {
    try {
      const { code } = values;
      setLoading(true);

      if (!code) {
        return
      }

      const verifyCode = await verifyService.verifyCode(userEmail, code)

      if (!verifyCode.status && verifyCode.message === 'invalid code') {
        setLoading(false);
        return errorNotify(' Не верный код подтверждения, попробуйте еще раз');
      }

      if (!verifyCode.status) {
        throw new Error(verifyCode.message);
      }

      await registerUser(userEmail, userPassword, userTelegramId)
    } catch (error) {
      setLoading(false);
      console.error(error.message);
      errorNotify('Возникла непредвиденная ошибка.');
    }
  }

  useEffect(() => {
    dispatch(getLogos(['p2pLogo']));
  }, [dispatch])

  const selectedLogos = useSelector((state: RootState) => state.images);
  return (
    <div className={style.container}>
      <div className={style.logoContainer}>
        <img src={selectedLogos.p2pLogo} alt="Logo" className={style.logo} />
      </div>

      <div className={style.content}>
        <h1 className={style.text}>Создайте новый аккаунт</h1>
        <text className={style.text}>Это быстро и просто.</text>

        <Card sx={{ maxWidth: 400, margin: 'auto', mt: 5, p: 2 }}>
          {!onSendCode
            ? (<CardContent>
              <Typography variant="h5" align="center" gutterBottom>
                Регестрация
              </Typography>
              <Formik
                initialValues={{ email: '', password: '', telegramId: null }}
                validationSchema={validationUserDataSchema}
                onSubmit={async (values) => {
                  await handlerCheckEmail(values)
                }}
              >
                {({ errors, touched, handleChange }) => (
                  <Form>
                    <Field
                      as={TextField}
                      label="Email"
                      name="email"
                      fullWidth
                      margin="normal"
                      error={touched.email && Boolean(errors.email)}
                      helperText={touched.email && errors.email}
                      onChange={handleChange}
                    />
                    <Field
                      as={TextField}
                      label="Пароль"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      fullWidth
                      margin="normal"
                      error={touched.password && Boolean(errors.password)}
                      helperText={touched.password && errors.password}
                      onChange={handleChange}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={togglePasswordVisibility} edge="end">
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Field
                      as={TextField}
                      label="Telegram ID"
                      name="telegramId"
                      fullWidth
                      margin="normal"
                      error={touched.email && Boolean(errors.email)}
                      helperText={touched.email && errors.email}
                      onChange={handleChange}
                    />
                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      loading={loading}
                      sx={{ mt: 2 }}
                    >
                      Зарегестрироваться
                    </Button>
                    <Typography variant="body2" align="center" sx={{ mt: 2 }}>
                      Уже есть акаунт? <Link to="/login">Войти</Link>
                    </Typography>
                  </Form>
                )}
              </Formik>
            </CardContent>)
            : (<CardContent>
              <Typography variant="h5" align="center" gutterBottom>
                Ведите код подтверждения отправленный на почту {userEmail}
              </Typography>
              <Formik
                key={'code-form'}
                initialValues={{ code: null }}
                validationSchema={validationCodeSchema}
                onSubmit={(values) => {
                  handleSubmitCode(values);
                }}
              >
                {({ errors, touched, handleChange }) => (
                  <Form>
                    <Field
                      as={TextField}
                      label="Code"
                      name="code"
                      fullWidth
                      margin="normal"
                      error={touched.code && Boolean(errors.code)}
                      helperText={touched.code && errors.code}
                      onChange={handleChange}
                      autoComplete="off"
                    />
                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      loading={loading}
                      sx={{ mt: 2 }}
                    >
                      Войти
                    </Button>
                    <Typography variant="body2" align="center" sx={{ mt: 2 }}>
                      Уже есть акаунт? <Link to="/login">Войти</Link>
                    </Typography>
                  </Form>
                )}
              </Formik>
            </CardContent>)}
        </Card>
      </div>
      <ToastContainer
        position="top-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Bounce}
      />
    </div>
  )
}

export default RegisterPage